import { Box, Button, IconButton, Typography, Grid, Modal } from '@mui/material';
import Menu from '@mui/material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useRecoilRefresher_UNSTABLE,
} from 'recoil';
import useDebounce from '../../Shared/Delay/index.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import UploadFile from './UploadFile.tsx';
import FilePreviewModal from './DocumentModel.tsx';
import {
  entityDetailsSelectorFamily,
  generateFilterKey,
} from '../../../Services/_states/Entity/entityDetailsSelector.ts';
import { entityDetailsStateAtomFamily } from '../../../Services/_states/index.js';
import CommonTable, { EllipsisWithTooltip } from '../../Shared/Tables/TableV1.tsx';
import { CardHeader } from 'react-bootstrap';
import { convertToReadableFormat, dateFormate } from '../../../utils/helper.jsx';
import { Tooltip } from '@mui/material';

const Details: React.FC = () => {
  const actions = EntityActions();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const { entityFormId } = useParams();
  const uniqueKey = entityFormId || 'default_entity_key'; // ensure it's always available
  const [tableState, setTableState] = useRecoilState(entityDetailsStateAtomFamily(uniqueKey));
  const fetchedData = useRecoilValueLoadable(entityDetailsSelectorFamily(uniqueKey));
  const refreshDetails = useRecoilRefresher_UNSTABLE(entityDetailsSelectorFamily(uniqueKey));
  // const { state } = useLocation();
  const [entityFormData, setEntityFormData] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [FormToDelete, setFormToDelete] = useState();
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [filePreview, setFilePreview] = useState({ open: false, fileUrl: '', fileType: '' });
  const [searchQuery, setSearchQuery] = useState(tableState?.filters?.search || '');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [formFieldTypes, setFormFieldTypes] = useState({});

  const getEntityFormData = async () => {
    try {
      const response = await actions.getFormById(entityFormId || '');
      if (response.status === 200) {
        setEntityFormData(response?.data?.items || {});
      } else {
        showSnackBar('Failed to fetch entity form data.', 'error');
      }
    } catch (error) {
      showSnackBar('An error occurred while fetching entity form data.', 'error');
    }
  };
  useEffect(() => {
    if (entityFormId) getEntityFormData();
    // const formField = state?.form_fields ?? [];
    // const fieldType = { ...formFieldTypes };
    // formField.map((item) => {
    //   fieldType[item?.name] = item?.type;
    // });
    // setFormFieldTypes(fieldType);
  }, [entityFormId]);

  const formatDateLocal = (value: any) => {
  if (!value || String(value).toLowerCase() === 'null') return '';

  const date = new Date(String(value));
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};


  const resetTableState = () => {
    setTableState((prev) => ({
      ...prev,
      cache: {},
      totalCountCache: {},
      filters: {
        limit: 20,
        search: '',
        page: 1,
        sortOrder: 'desc',
        sortField: 'created_at',
      },
    }));
  };
  const handleClickEdit = (data) => {
    navigate(`/r/datasets/data/${data._id}/${entityFormId}`, { state: { row: data } });
    resetTableState();
  };
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Datasets', href: '/r/datasets' },
    { label: 'Data' },
  ];
  const handleDelete = (data: any) => {
    setFormToDelete(data);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed && FormToDelete) {
      try {
        const res = await actions.deleteEntityDetail(FormToDelete?._id);

        if (res?.status === 200) {
          showSnackBar('Deleted entity successfully.', 'success');
          const filterKey = generateFilterKey(tableState.filters);
          const updatedItems =
            tableState.cache[filterKey]?.filter((item) => item._id !== FormToDelete?._id) || [];
          setTableState((prev) => ({
            ...prev,
            cache: { ...prev.cache, [filterKey]: updatedItems },
            totalCountCache: {
              ...prev.totalCountCache,
              [filterKey]: (prev.totalCountCache[filterKey] || 0) - 1,
            },
            filters: {
              ...prev.filters,
              page:
                updatedItems.length === 0 && prev.filters.page > 1
                  ? prev.filters.page - 1
                  : prev.filters.page,
            },
          }));
          const prevFilterKey = generateFilterKey({
            ...tableState.filters,
            page: tableState.filters.page - 1,
          });

          if (tableState.cache[prevFilterKey]) {
            return;
          }
        } else {
          showSnackBar('Failed to delete entity. Please try again.', 'error');
        }
      } catch (error) {
        showSnackBar('An error occurred while deleting the entity.', 'error');
      } finally {
        setConfirmOpen(false);
      }
    } else {
      setConfirmOpen(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          search: debouncedSearch || '',
          // page: 0
          page: prev.filters.page,
        },
        cache: {},
      }));
    }
  }, [debouncedSearch, setTableState]);

  const redirectToDetail = (data: any | null) => {
    navigate(`/r/datasets/data/${data?._id || 'create'}/${entityFormId}`, {
      state: { row: data, showBackButton: true },
    });
    resetTableState();
  };

  const formFieldLabels = useMemo(() => {
    const map: Record<string, string> = {};
    (entityFormData?.form_fields ?? []).forEach((f: any) => {
      if (f?.name) map[f.name] = f?.label || f.name;
    });
    return map;
  }, [entityFormData?.form_fields]);

  const currentData =
    tableState?.cache[generateFilterKey(tableState?.filters)] || tableState?.items || [];

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    if (!currentData.length) return [];

    const entityKeys = new Set<string>();
    currentData.forEach((row) => {
      Object.keys(row?.entity ?? {}).forEach((k) => entityKeys.add(k));
    });

    const entityColumns: MRT_ColumnDef<any>[] = [...entityKeys].map((key) => {
      if (key === 'created_at' || key === 'updated_at') {
        const niceHeader = key === 'created_at' ? 'Created At' : 'Updated At';
        return {
          accessorKey: `entity.${key}`,
          header: niceHeader,
          enableSorting: false,
          size: 150,
          Cell: ({ row }) => {
            const topLevel = row.original?.[key];
            const nested = row.original?.entity?.[key];

            const val = topLevel ?? nested;

            if (!val || String(val).toLowerCase() === 'null') return '';

            return dateFormate(val);
          },
        };
      }

      return {
        accessorKey: `entity.${key}`,
        header: formFieldLabels[key] || convertToReadableFormat(key),
        enableSorting: false,
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (['image', 'video', 'document']?.includes(formFieldTypes?.[key])) {
            return (
              <Button
                size="small"
                onClick={() =>
                  setFilePreview({ open: true, fileUrl: value, fileType: formFieldTypes?.[key] })
                }
              >
                View
              </Button>
            );
          }

          if (formFieldTypes?.[key] === 'date') {
  return formatDateLocal(value);
}


          if (formFieldTypes?.[key] === 'text' || formFieldTypes?.[key] === 'textArea') {
            return <EllipsisWithTooltip text={value} />;
          }

          return typeof value === 'object' ? JSON.stringify(value) : (value ?? '');
        },
      };
    });

    // const entityColumns = [...entityKeys].map((key) => ({
    //   accessorKey: `entity.${key}`,
    //   header: formFieldLabels[key] || convertToReadableFormat(key),
    //   enableSorting: false,
    //   size: 250,
    //   Cell: ({ cell }) => {
    //     const value = cell.getValue();
    //     if (['image', 'video', 'document']?.includes(formFieldTypes?.[key])) {
    //       return (
    //         <Button
    //           size="small"
    //           onClick={() =>
    //             setFilePreview({ open: true, fileUrl: value, fileType: formFieldTypes?.[key] })
    //           }
    //         >
    //           View
    //         </Button>
    //       );
    //     } else if (formFieldTypes?.[key] === "date") {
    //       return dateFormate(value);

    //     } else return typeof value === 'object' ? JSON.stringify(value) : value ?? '';
    //   },
    // }));

    const actionColumn: MRT_ColumnDef<any>[] = [
      {
        accessorKey: 'action',
        header: 'Action',
        size: 50,
        enableSorting: false,
        enableColumnDragging: false,
        enableHiding: false,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{ 'aria-labelledby': 'menu-button' }}
                // helps in virtualized tables / z-index oddities
                container={document.body}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    handleClickEdit(row?.original);
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    handleDelete(row.original);
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </div>
          );
        },
      },
    ];

    return [...entityColumns, ...actionColumn];
  }, [currentData, formFieldLabels, formFieldTypes]);

  const handleChangePage = (event, newPage) => {
    const nextFilters = {
      ...tableState.filters,
      page: newPage + 1,
    };
    const filterKey = generateFilterKey(nextFilters);
    const cachedData = tableState.cache[filterKey];

    setTableState((prev) => ({
      ...prev,
      filters: nextFilters,
      items: cachedData || [],
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value) ?? 20;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, limit: value, page: 1 },
    }));
  };

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        count: fetchedData.contents.totalCount,
        items: fetchedData.contents.data,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  useEffect(() => {
    if (entityFormId) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          entityFormId,
          page: 1,
        },
        cache: {},
        totalCountCache: {},
      }));
    }
  }, [entityFormId]);

  const onUploadSuccess = () => {
    setShowUploadFile(false);

    setTableState((prev) => ({
      ...prev,
      cache: {},
      totalCountCache: {},
      filters: {
        ...prev.filters,
        search: '',
        page: 1,
      },
    }));

    refreshDetails();
    // resetTableState();
  };
  return (
    <Box>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {entityFormData?.name || 'Data'}
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Tooltip
            title={
              entityFormData?.form_fields?.length <= 1
                ? 'At least 2 fields are required to create an entity'
                : ''
            }
          >
            <span>
              {' '}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  redirectToDetail(null);
                }}
                disabled={entityFormData?.form_fields?.length <= 1}
              >
                Create
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ ml: 2 }}
            onClick={() => setShowUploadFile(true)}
          >
            Upload File
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}
        className="search-box-style"
      >
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchBox handleSearch={handleSearch} value={searchQuery} />
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={currentData}
        // muiTableBodyCellProps={{
        //   sx: (theme) => ({
        //     backgroundColor:
        //       theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        //   }),
        // }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 260px)', width: '100%' } }}
        state={{ isLoading: fetchedData.state === 'loading' }}
        initialState={{
          columnPinning: {
            right: ['action'],
          },
          density: 'compact',
        }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
              rowsPerPage={tableState?.filters?.limit}
              page={tableState?.filters?.page - 1}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <Modal open={showUploadFile} onClose={() => setShowUploadFile(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: '8px',
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => setShowUploadFile(false)}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Upload File
          </Typography>
          <UploadFile onUploadSuccess={onUploadSuccess} />
        </Box>
      </Modal>
      <FilePreviewModal
        open={filePreview.open}
        handleClose={() => setFilePreview({ open: false, fileUrl: '', fileType: '' })}
        fileUrl={filePreview.fileUrl}
        fileType={filePreview.fileType}
      />
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete record',
          text: 'Are you sure you want to delete this record?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};
export default Details;
